import { useEffect, useRef } from "react";
import type { Version } from "@/hooks/roster-craete/useRosterVersions";
import {
  useScheduleStore,
  type CurDate,
  type CurScheduleInfo,
} from "@/store/scheduleStore";
import { SelectVersionHandler } from "@utils/rosterCreate-util";

type UseRosterVersionSelectionEffectsParams<TRosterData> = {
  readonly curDate: CurDate | null;
  readonly curScheduleInfo: CurScheduleInfo | null;
  readonly versionList: Version[] | undefined;
  readonly rosterData: TRosterData | undefined;
  readonly isLoading: boolean;
  readonly isVersionChanging: boolean;
  readonly clearHistory: () => void;
  readonly setUiMode: (mode: "none") => void;
  readonly setCurScheduleInfo: (info: CurScheduleInfo | null) => void;
  readonly setIsDateChanging: (value: boolean) => void;
  readonly setIsVersionChanging: (value: boolean) => void;
  readonly setIsChangeDateButtonClick: (value: boolean) => void;
  readonly resetEmergencyOnDateChange: () => void;
  readonly showWantedViolations: boolean;
  readonly showManpowerViolations: boolean;
  readonly showConfigViolations: boolean;
  readonly toggleWantedViolations: () => void;
  readonly toggleManpowerViolations: () => void;
  readonly toggleConfigViolations: () => void;
};

export function useRosterVersionSelectionEffects<TRosterData>({
  curDate,
  curScheduleInfo,
  versionList,
  rosterData,
  isLoading,
  isVersionChanging,
  clearHistory,
  setUiMode,
  setCurScheduleInfo,
  setIsDateChanging,
  setIsVersionChanging,
  setIsChangeDateButtonClick,
  resetEmergencyOnDateChange,
  showWantedViolations,
  showManpowerViolations,
  showConfigViolations,
  toggleWantedViolations,
  toggleManpowerViolations,
  toggleConfigViolations,
}: UseRosterVersionSelectionEffectsParams<TRosterData>) {
  const prevScheduleIdRef = useRef<string | undefined>(undefined);
  const prevRosterDataRef = useRef<TRosterData | undefined>(undefined);
  const prevDateKey = useRef("");
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const currentScheduleId = curScheduleInfo?.scheduleId;
    if (
      prevScheduleIdRef.current !== undefined &&
      prevScheduleIdRef.current !== currentScheduleId &&
      currentScheduleId
    ) {
      setIsVersionChanging(true);
      clearHistory();
      setUiMode("none");
      if (rosterData !== undefined) {
        prevRosterDataRef.current = rosterData;
      }
    }
    prevScheduleIdRef.current = currentScheduleId;
  }, [
    curScheduleInfo?.scheduleId,
    rosterData,
    clearHistory,
    setUiMode,
    setIsVersionChanging,
  ]);

  useEffect(() => {
    if (!isLoading && isVersionChanging && rosterData !== undefined) {
      setIsVersionChanging(false);
      prevRosterDataRef.current = undefined;
    }
  }, [isLoading, isVersionChanging, rosterData, setIsVersionChanging]);

  useEffect(() => {
    if (!curDate?.year || !curDate?.month) return;
    const currentDateKey = `${curDate.year}-${curDate.month}`;
    const dateChanged = prevDateKey.current !== currentDateKey;
    if (!dateChanged) return;

    if (showWantedViolations) toggleWantedViolations();
    if (!showManpowerViolations) toggleManpowerViolations();
    if (!showConfigViolations) toggleConfigViolations();
    clearHistory();
    setUiMode("none");
    setCurScheduleInfo(null);
    setIsDateChanging(true);
    prevDateKey.current = currentDateKey;
    isInitialLoad.current = false;
  }, [
    curDate?.year,
    curDate?.month,
    clearHistory,
    setUiMode,
    setCurScheduleInfo,
    setIsDateChanging,
    showWantedViolations,
    showManpowerViolations,
    showConfigViolations,
    toggleWantedViolations,
    toggleManpowerViolations,
    toggleConfigViolations,
  ]);

  useEffect(() => {
    if (
      curDate?.year &&
      curDate?.month &&
      !curScheduleInfo?.scheduleId &&
      versionList &&
      versionList.length > 0
    ) {
      setCurScheduleInfo(SelectVersionHandler(versionList, "current"));
    }
  }, [
    curDate?.year,
    curDate?.month,
    curScheduleInfo?.scheduleId,
    versionList,
    setCurScheduleInfo,
  ]);

  useEffect(() => {
    if (!versionList) return;

    const { removeDeletedScheduleId } = useScheduleStore.getState();
    versionList.forEach((v) => {
      removeDeletedScheduleId(v.schedule_id);
    });

    if (versionList.length > 0) {
      if (isInitialLoad.current || !curScheduleInfo?.scheduleId) {
        setCurScheduleInfo(SelectVersionHandler(versionList, "current"));
      } else {
        const currentExists = versionList.some(
          (v) => v.schedule_id === curScheduleInfo.scheduleId
        );
        if (!currentExists) {
          setCurScheduleInfo(SelectVersionHandler(versionList, "current"));
        }
      }
    } else {
      setCurScheduleInfo(null);
    }

    setIsChangeDateButtonClick(false);
    setIsDateChanging(false);
    isInitialLoad.current = false;
  }, [
    versionList,
    curScheduleInfo?.scheduleId,
    setCurScheduleInfo,
    setIsChangeDateButtonClick,
    setIsDateChanging,
  ]);

  useEffect(() => {
    if (curDate?.year && curDate?.month) {
      setUiMode("none");
      resetEmergencyOnDateChange();
    }
  }, [curDate?.year, curDate?.month, setUiMode, resetEmergencyOnDateChange]);
}
